import { module as Module } from "modujs";
import * as CookieConsent from 'vanilla-cookieconsent';
import { language } from "../utils/cookieconsent";

export default class extends Module {
    constructor(m) {
        super(m);

        this.events = {
            click: {
                'show-preferences': 'showPreferences'
            }
        }

        // Strict to true complies with Law 25 (Quebec). If false, complies with GDPR.
        this.isStrict = this.getData('strict') === 'true';
    }

    async init() {
        /**
         * Load Shopify Consent Tracking API
         * @see https://shopify.dev/docs/api/consent-tracking
         */
        await window.Shopify.loadFeatures(
            [
                {
                    name: 'consent-tracking-api',
                    version: '0.1',
                },
            ],
            (error) => {
                if (error) {
                    console.error('Error loading Shopify features', error);
                    return;
                }
            }
        );

        this.initCookieConsent();
    }

    /**
     * Initialize Cookie Consent
     */
    initCookieConsent() {
        /**
         * All config. options available here:
         * @see https://cookieconsent.orestbida.com/reference/configuration-reference.html
         */
        CookieConsent.run({
            // Callbacks
            onConsent: () => {
                this.toggleAllShopifyCategories();
            },
            onChange: ({ changedCategories }) => {
                if (changedCategories.includes('marketing')) {
                    this.toggleShopifyConsentCategory('marketing');
                }

                if (changedCategories.includes('analytics')) {
                    this.toggleShopifyConsentCategory('analytics');
                }

                if (changedCategories.includes('preferences')) {
                    this.toggleShopifyConsentCategory('preferences');
                }

                if (changedCategories.includes('sale_of_data')) {
                    this.toggleShopifyConsentCategory('sale_of_data');
                }
            },
            // Configuration
            disablePageInteraction: false,
            guiOptions: {
                consentModal: {
                    layout: 'box inline',
                    equalWeightButtons: false
                },
                preferencesModal: {
                    equalWeightButtons: false
                }
            },
            // Categories
            categories: {
                necessary: {
                    enabled: true,
                    readOnly: true
                },
                marketing: {
                    enabled: CookieConsent?.acceptedCategory('marketing'),
                },
                analytics: {
                    enabled: CookieConsent?.acceptedCategory('analytics'),
                    autoClear: {
                        cookies: [
                            {
                                name: /^(_ga)/ // Regex
                            },
                            { name: '_landing_page' },
                            { name: '_orig_referrer' },
                            { name: '_s' },
                            { name: '_shopify_d' },
                            { name: '_shopify_fs' },
                            { name: '_shopify_s' },
                            { name: '_shopify_sa_p' },
                            { name: '_shopify_sa_t' },
                            { name: '_shopify_y' },
                            { name: '_y' },
                            { name: '_shopify_ga' },
                            { name: 'customer_auth_provider' },
                            { name: 'customer_auth_session_created_at' },
                            { name: 'shop_analytics' },
                            { name: 'unique_interaction_id' }
                        ]
                    }
                },
                preferences: {
                    enabled: CookieConsent?.acceptedCategory('preferences'),
                },
                sale_of_data: {
                    enabled: CookieConsent?.acceptedCategory('sale_of_data'),
                }
            },
            language: language
        });

        // If strict mode is enabled and never interacted with Privacy consent, disable all the categories by default
        if (this.isStrict && window.Shopify.customerPrivacy.getTrackingConsent() == 'no_interaction') {
            console.log('[Shopify Cookie Consent Manager]: Strict mode enabled, disabling all categories by default');
            this.toggleAllShopifyCategories();
        }
    }

    /**
     * Toggle Shopify Consent Tracking API category
     * @param {string} category
     */
    toggleShopifyConsentCategory(category) {
        const value = CookieConsent.acceptedCategory(category);
        const consentData = { [category]: value };

        window.Shopify?.customerPrivacy.setTrackingConsent(consentData, () => {
            console.log(`[Shopify Cookie Consent Manager]: ${category} consent set to ${value}`);
        });
    }

    /**
     * Toggle all Shopify Consent Tracking API categories
     */
    toggleAllShopifyCategories() {
        const categories = {
            marketing:    CookieConsent?.acceptedCategory('marketing'),
            analytics:    CookieConsent?.acceptedCategory('analytics'),
            preferences:  CookieConsent?.acceptedCategory('preferences'),
            sale_of_data: CookieConsent?.acceptedCategory('sale_of_data'),
        };
        window.Shopify?.customerPrivacy.setTrackingConsent(
            categories,
            () => {
                console.log('[Shopify Cookie Consent Manager]: All consents updated:', categories)
            }
        );
    }

    /**
     * Open Cookie Consent preferences modal
     */
    showPreferences() {
        CookieConsent?.showPreferences();
    }

    destroy() {
        super.destroy();
    }
}
